import { NumericBoolean } from '@bilibili-maoer/web-utils'

import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Collapse,
  List,
  ListItem as MuiListItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  css,
  styled,
} from '@mui/material'

import { notificationUnreadMark } from '../styles'

import { StyledDarkListItemButton } from '~/core/components/list'
import useAppNavigate from '~/shared/navigation/use-app-navigate'

interface Route {
  text: string
  to?: string
  collapsed?: boolean
  children?: Routes
  check?: () => boolean
  notice?: NumericBoolean
}

type RouteWithIcon = { icon?: JSX.Element } & Route

export type Routes = Route[] | RouteWithIcon[]

const isDescendant = (parent: Route | RouteWithIcon, childPath: string): boolean => {
  if (parent.children) {
    // eslint-disable-next-line no-restricted-syntax
    for (const child of parent.children) {
      if (child.to === childPath || isDescendant(child, childPath)) {
        return true
      }
    }
  }

  return false
}

const StyledListItemIcon = styled(MuiListItemIcon)`
  transition: color 0.1s ease, opacity 0.1s ease;
`

const ListItemText = styled(MuiListItemText)<{ notice: NumericBoolean }>(
  ({ notice }) => css`
    transition: color 0.1s ease, opacity 0.1s ease;
    ${!!notice &&
    css`
      ${notificationUnreadMark({ top: '-2px' })}
      & > span {
        display: inline-block;
      }
    `}
  `
)

const Icon = (props: { sidebarOpen: boolean; active: boolean; route: Route | RouteWithIcon }) => {
  if (!('icon' in props.route)) return <></> // 如果没有配置图标就不渲染了

  return (
    <StyledListItemIcon
      sx={{
        minWidth: props.sidebarOpen ? '0' : '100%',
        color: props.active ? 'primary.main' : 'white', // 因为是深色的背景，所以这里的颜色是白色
        justifyContent: props.sidebarOpen ? 'initial' : 'center', // 否则会出现偏移和不居中的问题
        width: 20,
        height: 20,
      }}
    >
      {props.route.icon}
    </StyledListItemIcon>
  )
}

const ListItem = (props: {
  sidebarOpen: boolean
  route: Route | RouteWithIcon
  level: number
  collapsed?: boolean
  onClick?: () => void
}) => {
  const navigate = useAppNavigate()
  const { pathname } = useLocation()
  const collapsible = useMemo(() => props.collapsed !== undefined, [props.collapsed])
  const active = useMemo(
    () => (collapsible ? false : pathname === props.route.to),
    [collapsible, pathname, props.route.to]
  )
  const handleListItemClick = () =>
    collapsible ? props.onClick && props.onClick() : props.route.to && navigate(props.route.to)

  return (
    <MuiListItem disablePadding>
      <StyledDarkListItemButton
        onClick={handleListItemClick}
        color="secondary"
        sx={{
          justifyContent: props.sidebarOpen ? 'initial' : 'center', // 否则会出现偏移和不居中的问题
          paddingLeft: props.sidebarOpen ? `${props.level * 2}rem` : 0, // 缩进
          paddingRight: props.sidebarOpen ? '0.5rem' : 0, // 右边距
        }}
      >
        <Icon sidebarOpen={props.sidebarOpen} active={active} route={props.route} />
        <ListItemText
          primary={props.route.text}
          sx={{
            color: active ? 'primary.main' : 'white',
            opacity: props.sidebarOpen ? 1 : 0, // 侧边栏关闭时隐藏文字
            marginLeft: '10px',
          }}
          notice={props.route?.notice || 0}
        />
        {collapsible && (
          <ListItemCollapsibleButtonIcon sidebarOpen={props.sidebarOpen} collapsed={props.collapsed || false} />
        )}
      </StyledDarkListItemButton>
    </MuiListItem>
  )
}

const ListItemCollapsibleButtonIcon = (props: { collapsed: boolean; sidebarOpen: boolean }) => {
  if (!props.sidebarOpen) return <></>
  return props.collapsed ? <ExpandMore /> : <ExpandLess />
}

export const SidebarNavItem = (props: { sidebarOpen: boolean; route: Route | RouteWithIcon; level: number }) => {
  const [collapsed, setCollapsed] = useState(!!props.route.collapsed) // 默认折叠
  const [wasCollapsed, setWasCollapsed] = useState(!!props.route.collapsed) // 默认折叠
  const { pathname } = useLocation()

  const isHideNavItem = (route?: Route) => !!route?.check && route.check() === false
  // 如果任一一个子级被选中，那么就展开
  useEffect(() => {
    if (props.route.children && isDescendant(props.route, pathname)) {
      setCollapsed(false)
    }
  }, [pathname, props.route.children, props.route, props.sidebarOpen])

  // 如果折叠状态被修改，那么就更新 wasCollapsed
  useEffect(() => {
    if (wasCollapsed !== collapsed) {
      setWasCollapsed(collapsed)
    }
  }, [collapsed, wasCollapsed])

  // 如果 sidebarOpen 发生了变化，那么：
  // 1. 如果是收起 sidebar，那么关闭 level 为 1 的所有折叠菜单
  // 2. 如果是展开 sidebar，那么恢复 level 为 1 的所有折叠菜单的状态
  useEffect(() => {
    if (props.level === 1) {
      setCollapsed(!props.sidebarOpen)
    }
  }, [props.sidebarOpen, props.level, props.route])

  if (props.route.children && props.route.children.length > 0) {
    return (
      <>
        <ListItem
          sidebarOpen={props.sidebarOpen}
          route={props.route}
          level={props.level}
          collapsed={collapsed}
          onClick={() => {
            setCollapsed(!collapsed)
          }}
        />
        <Collapse in={!collapsed} timeout="auto">
          <List
            sx={{
              flex: '1', // 侧边栏的按钮部分高度自适应
            }}
            disablePadding
          >
            {props.route.children.map((route, index) =>
              isHideNavItem(route) ? null : (
                <SidebarNavItem level={props.level + 1} key={index} sidebarOpen={props.sidebarOpen} route={route} />
              )
            )}
          </List>
        </Collapse>
      </>
    )
  }

  return isHideNavItem(props?.route) ? null : (
    <ListItem key={props.route.to} sidebarOpen={props.sidebarOpen} route={props.route} level={props.level} />
  )
}

export const SidebarNav = (props: { sidebarOpen: boolean; routes: Routes }) => (
  <List
    sx={{
      flex: '1', // 侧边栏的按钮部分高度自适应
    }}
  >
    {props.routes.map((route, index) => (
      <SidebarNavItem key={index} sidebarOpen={props.sidebarOpen} route={route} level={1} />
    ))}
  </List>
)
